// CC: taken from exchange/gsuite/ldap-integration components, simplified for onboarding
import * as util from 'util';
import _ from 'lodash';
import {Component, EventEmitter, Input, NgZone, OnInit, Output} from "@angular/core";
import {GeneralService} from '../../../services/generalService';
import {AuthService} from '../../../services/authService';
import {RouteService} from '../../../services/routeService';
import {NotificationService} from '../../../services/notificationService';
import {DICTIONARY} from '../../../dictionary';

@Component({
	selector: 'onboarding-connect-api-integration-component',
	templateUrl: './onboarding-connect-api-integration.component.html',
})
export class OnboardingConnectApiIntegrationComponent implements OnInit {
	@Input() integrationType: string;
	@Output() connectionChanged = new EventEmitter<void>();

	dic = DICTIONARY;
	planInfo;
	userInfo;
	selectedServerEndpoint;
	changeEndPointPopup;
	customCredentialsEndpoints = [DICTIONARY.CONSTANTS.exchangeServerEndpoints.USGovernmentGCCHigh.name, DICTIONARY.CONSTANTS.exchangeServerEndpoints.USGovernmentDoD.name, DICTIONARY.CONSTANTS.exchangeServerEndpoints.China.name, DICTIONARY.CONSTANTS.exchangeServerEndpoints.SingleTenantApp.name];
	connectInProcess = false;
	advancedInProcess = false;
	connected = false;
	advancedEnabled = false;
	verifiedDomains = [];

	// Issue #15842: Advanced Permissions are selected by default (Exchange and Google), basic only is an explicit choice
	advancedPermissions = true;
	advancedPermissionsInfo = DICTIONARY.MESSAGES.advancedPermissionsInfo;

	// CC: LDAP-specific fields
	ldapForm = {
		username: '',
		password: '',
		endpoint: '',
		baseDN: '',
		secure: true,
		port: 636,
		showPassword: false
	};

	// CC: GSuite-specific fields
	gsuiteConfigPopup;
	gsuiteForm = {
		adminEmail: '',
		customerId: '',
		inputErrors: {
			emailInput: false,
			idInput: false
		}
	};

	// CC: taken from gsuite-integration.component.ts -> googleScopes
	googleScopes = `https://www.googleapis.com/auth/admin.directory.group.readonly,
					https://www.googleapis.com/auth/admin.directory.user.readonly,
					https://www.googleapis.com/auth/admin.directory.domain.readonly,
					https://www.googleapis.com/auth/admin.reports.audit.readonly,
					https://www.googleapis.com/auth/contacts.readonly,
					https://www.googleapis.com/auth/contacts.other.readonly,
					https://www.googleapis.com/auth/gmail.modify`;
	googleScopeActivity = 'https://www.googleapis.com/auth/admin.directory.user';
	googleScopesLine = this.googleScopes.split('\n').join('');
	googleAdvancedScopes = `${this.googleScopes},\n${this.googleScopeActivity}`;
	googleAdvancedScopesLine = this.googleAdvancedScopes.split('\n').join('');

	constructor(public gs: GeneralService,
				private authService: AuthService,
				private rs: RouteService,
				private ns: NotificationService,
				private ngZone: NgZone) {
	}

	ngOnInit() {
		this.userInfo = this.gs.userInfo;
		this.getIntegrations();
	}

	// CC: taken from exchange/gsuite/ldap-integration components -> getIntegrations
	getIntegrations = () => {
		return this.rs.getIntegrations().then((planInfo) => {
			this.planInfo = planInfo;
			this.planInfo.integrations = this.planInfo.integrations || {};

			if (this.integrationType === this.dic.CONSTANTS.integrationType.exchange) {
				this.selectedServerEndpoint = _.clone(this.dic.CONSTANTS.exchangeServerEndpoints.Global);
				if (this.planInfo.exchange_server?.endpoint_name) {
					this.selectedServerEndpoint.name = this.planInfo.exchange_server.endpoint_name;
					this.selectedServerEndpoint.AzureAD = this.planInfo.exchange_server.azuread_endpoint;
					this.selectedServerEndpoint.MSGraphAPI = this.planInfo.exchange_server.msgraph_endpoint;
				}
				if (!this.selectedServerEndpoint.AzureAD) {
					this.selectedServerEndpoint.AzureAD = DICTIONARY.CONSTANTS.exchangeServerEndpoints.Global.AzureAD;
				}
				this.connected = !!(this.planInfo.exchange_server?.tid && this.planInfo.exchange_server?.enabled);
				this.advancedEnabled = !!this.planInfo.exchange_server?.advanced_enabled;
				this.verifiedDomains = this.planInfo.exchange_server?.verified_domains || [];
			}
			else if (this.integrationType === this.dic.CONSTANTS.integrationType.gsuite) {
				this.connected = !!(this.planInfo.gsuite_server?.customer_id && this.planInfo.gsuite_server?.enabled);
				this.advancedEnabled = !!this.planInfo.gsuite_server?.scopes?.includes('users_modify');
				this.verifiedDomains = this.planInfo.gsuite_server?.verified_domains || [];
			}
			else if (this.integrationType === this.dic.CONSTANTS.integrationType.ldap) {
				this.connected = !!this.planInfo.integrations.ldap?.enabled;
			}
		});
	}

	// CC: taken from exchange-integration.component.ts -> connectToExchange (connect-only)
	connectExchange = () => {
		if (this.connectInProcess) {
			return;
		}
		// CC: taken from exchange-integration.component.ts -> connectToExchange (single-tenant cloud without app credentials)
		if (this.customCredentialsEndpoints.includes(this.selectedServerEndpoint.name) && !this.planInfo.exchange_server?.client_id) {
			this.changeEndpoint(this.selectedServerEndpoint);
			return;
		}

		this.connectInProcess = true;
		// Issue #15842: the advanced app holds the basic permissions too, so a single consent grants both
		const appId = this.advancedPermissions ? this.planInfo.exchangeAdminAdvancedAppId : this.planInfo.exchangeAdminAppId;
		this.authService.authMicrosoftExchangeAdmin(this.userInfo.email, this.selectedServerEndpoint.AzureAD, appId, this.advancedPermissions ? 'advanced' : 'admin', this.planInfo.exchange_server?.tid, (err) => {
			if (err) {
				if (err !== true) {
					this.ns.showWarnMessage(err);
				}
			}

			this.ngZone.run(() => {
				this.connectInProcess = false;
				// Issue #15842: when only the User Administrator role fails on a first connection, the server connects with basic permissions - the refreshed plan tells which one
				this.getIntegrations().then(() => {
					if (err) {
						return;
					}
					if (this.advancedPermissions && !this.advancedEnabled) {
						this.ns.showWarnMessage('Connected to Microsoft Exchange with basic permissions. Advanced Permissions could not be granted yet - click Grant Advanced Permissions to try again.');
					}
					else {
						this.ns.showInfoMessage(this.advancedPermissions ? 'Successfully connected to Microsoft Exchange with Advanced Permissions' : 'Successfully connected to Microsoft Exchange');
					}
				});
				this.connectionChanged.emit();
			});
		});
	}

	// CC: taken from exchange-integration.component.ts -> connectToExchangeAdvanced
	connectExchangeAdvanced = () => {
		if (this.advancedInProcess) {
			return;
		}

		this.advancedInProcess = true;
		this.authService.authMicrosoftExchangeAdmin(this.userInfo.email, this.selectedServerEndpoint.AzureAD, this.planInfo.exchangeAdminAdvancedAppId, 'advanced', this.planInfo.exchange_server?.tid, (err) => {
			if (err) {
				if (err !== true) {
					this.ns.showWarnMessage(err);
				}
			}
			else {
				this.ns.showInfoMessage('Advanced permissions granted successfully');
			}

			this.ngZone.run(() => {
				this.advancedInProcess = false;
				this.getIntegrations();
			});
		});
	}

	// CC: taken from exchange-integration.component.ts -> changeEndpoint
	changeEndpoint = (endpoint) => {
		if (this.connectInProcess) {
			return;
		}
		const exchangeServer = this.planInfo.exchange_server || {};
		const usesCustomCredentials = this.customCredentialsEndpoints.includes(endpoint.name);
		if (this.selectedServerEndpoint.name === endpoint.name && !usesCustomCredentials) {
			return;
		}
		if (usesCustomCredentials) {
			this.changeEndPointPopup = {
				show: true,
				applyInProcess: false,
				endpoint,
				subTitle: `Configure credentials for ${endpoint.name}`,
				clientIdInput: exchangeServer.client_id || '',
				clientSecretInput: '',
				clientSecretSet: !!exchangeServer.client_secret_set
			};
			return;
		}

		let azureADInput: any;
		let microsoftGraphAPIInput: any;
		if (endpoint.name === this.dic.CONSTANTS.exchangeServerEndpoints.Hybrid.name) {
			azureADInput = '';
			microsoftGraphAPIInput = '';
		}

		this.changeEndPointPopup = {
			show: true,
			applyInProcess: false,
			subTitle: `Please note - you are about to change the cloud API endpoint of your mail server to ${endpoint.name}`,
			azureADInput: azureADInput,
			microsoftGraphAPIInput: microsoftGraphAPIInput,
			doneCb: () => {
				this.changeEndPointPopup.applyInProcess = true;
				const nextEndpoint = _.clone(endpoint);
				if (this.changeEndPointPopup.azureADInput !== undefined && this.changeEndPointPopup.microsoftGraphAPIInput !== undefined) {
					if (this.changeEndPointPopup.azureADInput === "" || this.changeEndPointPopup.microsoftGraphAPIInput === "") {
						this.changeEndPointPopup.applyInProcess = false;
						return this.ns.showErrorMessage("Please enter a valid endpoint");
					}

					if (this.changeEndPointPopup.azureADInput.indexOf(' ') > -1 || this.changeEndPointPopup.microsoftGraphAPIInput.indexOf(' ') > -1) {
						this.changeEndPointPopup.applyInProcess = false;
						return this.ns.showErrorMessage(this.dic.ERRORS.invalidServerEndpoint);
					}
					nextEndpoint.AzureAD = this.changeEndPointPopup.azureADInput;
					nextEndpoint.MSGraphAPI = this.changeEndPointPopup.microsoftGraphAPIInput;
				}
				this.updateCloudAPIEndpoint(nextEndpoint);
			}
		};
	}

	// CC: taken from exchange-integration.component.ts -> saveSovereignCloud
	saveSovereignCloud = () => {
		const popup = this.changeEndPointPopup;
		const exchangeServer = this.planInfo.exchange_server || {};
		const sameCloud = exchangeServer.endpoint_name === popup.endpoint.name;
		const sameMainCredentials = sameCloud && exchangeServer.client_id?.toLowerCase() === popup.clientIdInput?.toLowerCase();
		if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(popup.clientIdInput || '')) {
			return this.ns.showErrorMessage('The client ID must be a valid GUID.');
		}
		if (!popup.clientSecretInput && (!popup.clientSecretSet || !sameMainCredentials)) {
			return this.ns.showErrorMessage('Enter the client secret for this cloud and app registration.');
		}

		popup.applyInProcess = true;
		this.updateCloudAPIEndpoint({
			name: popup.endpoint.name,
			client_id: popup.clientIdInput,
			client_secret: popup.clientSecretInput
		});
	}

	// CC: taken from exchange-integration.component.ts -> updateCloudAPIEndpoint
	updateCloudAPIEndpoint = (endpoint) => {
		if (!endpoint?.name) {
			return;
		}

		this.rs.activateInboundEmailRelayAndGenKey({
			action: this.dic.CONSTANTS.inboundRelayActions.cloudAPIEndpoint,
			endpoint
		}).then(() => this.getIntegrations()).then(() => {
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
			this.changeEndPointPopup.show = false;
		}, () => {
			this.changeEndPointPopup.applyInProcess = false;
		});
	}

	showGsuiteConfigPopup = () => {
		this.gsuiteConfigPopup = {show: true};
	}

	// CC: taken from gsuite-integration.component.ts -> connectGSuite
	connectGsuite = () => {
		if (this.connectInProcess) {
			return;
		}

		this.gsuiteForm.inputErrors = {emailInput: false, idInput: false};

		if (!this.gsuiteForm.customerId || !this.gsuiteForm.adminEmail) {
			this.ns.showWarnMessage(this.dic.ERRORS.fillAllRequired);
			this.gsuiteForm.inputErrors.emailInput = !this.gsuiteForm.adminEmail;
			this.gsuiteForm.inputErrors.idInput = !this.gsuiteForm.customerId;
			return;
		}
		if (!this.gs.validateEmail(this.gsuiteForm.adminEmail)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidEmail, this.gsuiteForm.adminEmail));
			this.gsuiteForm.inputErrors.emailInput = true;
			return;
		}

		this.connectInProcess = true;

		const connectInfo = {
			adminEmail: this.gsuiteForm.adminEmail,
			customerId: this.gsuiteForm.customerId
		};
		this.rs.connectGSuite(connectInfo).then(() => {
			this.gsuiteConfigPopup = null;
			this.ns.showInfoMessage('Successfully connected to Google Workspace');
			this.ngZone.run(() => {
				this.connectInProcess = false;
				// Issue #15842: the server rejects missing basic scopes, the advanced scope is checked here
				this.getIntegrations().then(() => {
					if (this.advancedPermissions && !this.advancedEnabled) {
						this.ns.showWarnMessage(this.googleAdvancedMissingMessage());
					}
				});
				this.connectionChanged.emit();
			});
		}, (err) => {
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
			this.connectInProcess = false;
		});
	}

	// CC: taken from gsuite-integration.component.ts -> verifyGsuiteScope
	verifyGsuiteAdvanced = () => {
		if (this.advancedInProcess) {
			return;
		}

		this.advancedInProcess = true;
		this.rs.verifyGSuite().then((existingScopes) => {
			this.planInfo.gsuite_server.scopes = existingScopes;
			this.advancedEnabled = existingScopes.includes('users_modify');
			if (this.advancedEnabled) {
				this.ns.showInfoMessage('Advanced Permissions authorized successfully');
			}
			else {
				this.ns.showWarnMessage(this.googleAdvancedMissingMessage());
			}
			this.advancedInProcess = false;
		}, (err) => {
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
			this.advancedInProcess = false;
		});
	}

	googleAdvancedMissingMessage = () => {
		return `Advanced Permissions are not authorized. Add the scope ${this.googleScopeActivity} to the ${this.gs.corpname} client in Domain-Wide Delegation, then click Verify. Changes in Google can take a few minutes to apply.`;
	}

	// CC: taken from ldap-integration.component.ts -> connectLdapExecute
	connectLdap = () => {
		if (this.connectInProcess) {
			return;
		}

		if (!this.ldapForm.username || !this.ldapForm.password || !this.ldapForm.endpoint) {
			this.ns.showWarnMessage(this.dic.ERRORS.fillAllRequired);
			return;
		}

		this.connectInProcess = true;

		const endpoint = `ldap${this.ldapForm.secure ? 's' : ''}://${this.ldapForm.endpoint}:${this.ldapForm.port}`;

		const ldapInfo = {
			username: this.ldapForm.username,
			password: this.ldapForm.password,
			endpoint: endpoint,
			baseDN: this.ldapForm.baseDN,
		};
		this.rs.connectLDAP(ldapInfo).then(() => {
			this.ns.showInfoMessage('Successfully connected to LDAP');
			this.planInfo.integrations.ldap = ldapInfo;
			this.planInfo.integrations.ldap.enabled = true;
			this.connectInProcess = false;
			this.connected = true;
			this.connectionChanged.emit();
		}, (err) => {
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
			this.connectInProcess = false;
		});
	}
}
